import { Component, OnInit, OnDestroy, AfterViewInit, ViewChildren, ViewChild, QueryList, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DrmCapabilityService } from 'src/app/_service/drm-capability.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { TVNavigationService, Region, Zone } from 'src/app/_service/tv-navigation.service';
import { URLS } from 'src/app/constants/URLS';


@Component({
  selector: 'app-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.css']
})
export class SideBarComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChildren('menuItem') menuItems!: QueryList<ElementRef>;
  @ViewChild('sidebarContainer') sidebarContainer!: ElementRef;

  channelIdCache = '';
  channelUrl: string = URLS.channelUrl;
  persistentSupport = false;

  // TV Navigation state
  private destroy$ = new Subject<void>();
  focusedIndex = -1;  // -1 means no focus (not in sidebar zone)
  isActiveSidebarZone = false;

  constructor(
    public wl: WhiteLabelService,
    public device: DeviceService,
    private drmCapability: DrmCapabilityService,
    private tvNav: TVNavigationService
  ) {
    this.channelIdCache = localStorage.getItem('wl_content_provider_id') || '';
  }
  
  async ngOnInit() {
    this.persistentSupport = await this.drmCapability.supportsPersistentLicense();
  }

  ngAfterViewInit(): void {
    if (!this.device.isTV()) return;

    // Wait for menu items to render
    setTimeout(() => {
      this.registerSidebar();
    }, 100);

    // Listen for dynamic item changes (e.g., conditional items)
    this.menuItems.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.registerSidebar();
      });

    // Listen for zone changes to update visual state
    this.tvNav.zone$
      .pipe(takeUntil(this.destroy$))
      .subscribe(zone => {
        this.isActiveSidebarZone = zone === Zone.SIDEBAR;
        // Reset focus index display when leaving sidebar zone
        if (!this.isActiveSidebarZone) {
          this.focusedIndex = -1;
        }
      });

    // Listen for sidebar focus index changes
    this.tvNav.sidebarFocusIndex$
      .pipe(takeUntil(this.destroy$))
      .subscribe(index => {
        this.focusedIndex = this.isActiveSidebarZone ? this.mapToTemplateIndex(index) : -1;
      });
  }

  private registerSidebar(): void {
    if (this.menuItems.length === 0 || !this.sidebarContainer) return;

    const region: Region = {
      id: 'main-sidebar',
      type: 'sidebar',
      elementRef: this.sidebarContainer,
      focusableItems: this.menuItems.toArray()
    };
    this.tvNav.registerRegion(region);
  }

  ngOnDestroy(): void {
    // TV Navigation cleanup
    this.destroy$.next();
    this.destroy$.complete();

    if (this.device.isTV()) {
      this.tvNav.unregisterRegion('main-sidebar');
    }
  }

  /**
   * Map nav service array index to template hardcoded index.
   * Template indices: 0=Vids, 1=Flix/Home, 2=Search, 3=Downloads(conditional), 4=MySpace
   * Builds the list dynamically based on which items are actually visible
   * (white-label may hide Vids or Flix).
   */
  private mapToTemplateIndex(arrayIndex: number): number {
    const templateIndices: number[] = [];
    if (this.wl.showVids) templateIndices.push(0);       // Vids
    if (this.wl.showFlix) templateIndices.push(1);       // Flix / Home
    templateIndices.push(2);                              // Search (always)
    if (this.persistentSupport) templateIndices.push(3);  // Downloads
    templateIndices.push(4);                              // My Space (always)
    return templateIndices[arrayIndex] ?? -1;
  }

  getLink(path: string): string {
    return this.wl.getPrefixedRoute(path);
  }

}
